/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * The note on a PT programme saying who can see it (DESIGN-SPEC §5.10, §9).
 */

import React, { useEffect, useId, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Popover,
  PopoverContent,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
  PrivateIcon,
} from "@lemonadetraining/web-ui";
import { useAppSelector } from "@lemonadetraining/web-shared";
import { isPtProgrammeTrustNoteEnabled } from "@lemonadetraining/contracts";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import { useUserAccess } from "../../hooks/useUserAccess";
import { loadBoxName, useBoxOwnerUID } from "../../hooks/useBoxOwnerUID";
import {
  resolveTrustNoteViewer,
  trustNoteCopy,
  whoCanSee,
  type TrustNoteProgramme,
} from "./trustNoteCopy";

interface Props {
  programme: TrustNoteProgramme;
}

const PtTrustNote: React.FC<Props> = ({ programme }) => {
  const { flags } = useFeatureFlags(true);
  const uid = useAppSelector((state) => state.user.user?.uid ?? null);
  const access = useUserAccess();
  const [open, setOpen] = useState(false);
  const titleID = useId();

  // Only the viewer's own gym: useUserAccess has already read that Box, so
  // the owner and name come from its cache rather than a new read.
  const enabled = isPtProgrammeTrustNoteEnabled(flags);
  const ownGym =
    enabled && programme.boxID && access.boxID === programme.boxID ? programme.boxID : null;
  const { ownerUID: gymOwnerUID } = useBoxOwnerUID(ownGym);
  // Keyed by gym so a name loaded for one programme never labels the next.
  const [loadedGym, setLoadedGym] = useState<{ id: string; name: string | null } | null>(null);
  const gymName = loadedGym && loadedGym.id === ownGym ? loadedGym.name : null;

  useEffect(() => {
    if (!ownGym) return;
    let isMounted = true;
    loadBoxName(ownGym).then((name) => {
      if (isMounted) setLoadedGym({ id: ownGym, name });
    });
    return () => {
      isMounted = false;
    };
  }, [ownGym]);

  if (!enabled || access.isLoading) return null;

  const viewer = resolveTrustNoteViewer({
    uid,
    isBoxAdmin: (access.isBoxAdmin || access.isOwner) && ownGym !== null,
    programme,
  });
  if (!viewer) return null;

  const coachName = programme.ownerName || programme.CoachName || "";
  const subscriberCount = programme.subscribers?.length ?? 0;
  const keptByGym =
    !!programme.previousOwnerUID && !!gymOwnerUID && programme.ownerUID === gymOwnerUID;
  const { row1, row2 } = trustNoteCopy(viewer, {
    coachName,
    subscriberCount,
    keptByGymName: keptByGym ? gymName : null,
  });
  const explanation = whoCanSee(viewer, { coachName, gymName, subscriberCount });

  return (
    <Alert role="status" data-testid="trust-note">
      <PrivateIcon aria-hidden="true" className="text-muted-foreground" />
      <AlertTitle>{row1}</AlertTitle>
      {/* col-start-2: the Alert's icon row-span does not apply, so pin the column. */}
      <AlertDescription className="col-start-2 flex flex-col items-start gap-1">
        <span>{row2}</span>
        {explanation && (
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              {/* text-foreground: the link variant's yellow fails AA on the card. */}
              <Button
                variant="link"
                className="min-h-11 px-0 text-foreground underline underline-offset-4"
              >
                What&apos;s included?
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" aria-labelledby={titleID} className="w-80">
              <PopoverHeader>
                <PopoverTitle id={titleID}>Who can see this programme</PopoverTitle>
              </PopoverHeader>
              <ul className="list-disc space-y-1 pl-5">
                {explanation.parties.map((party) => (
                  <li key={party}>{party}</li>
                ))}
              </ul>
              <p className="text-muted-foreground">{explanation.included}</p>
              {explanation.notAffected && (
                <p className="text-muted-foreground">{explanation.notAffected}</p>
              )}
              <Button className="min-h-11 self-end" onClick={() => setOpen(false)}>
                Got it
              </Button>
            </PopoverContent>
          </Popover>
        )}
      </AlertDescription>
    </Alert>
  );
};

/* Split so a non-PT programme never reads the flags or the gym. */
const TrustNote: React.FC<Props> = ({ programme }) =>
  programme?.isPT === true ? <PtTrustNote programme={programme} /> : null;

export default TrustNote;
